import React, { useMemo, useEffect, useState, useRef } from 'react';
import { DeviceUtil } from 'utils/deviceUtil';
import BaseballCount from '../count';
import style from './style.module.scss';
import clsx from 'clsx';
import { timeUtils } from 'utils/timeUtil';
import SportsTeamLogo from 'components/teamLogo';
import ReactTooltip from 'react-tooltip';
import { observer } from 'mobx-react';
import { StringUtil } from 'utils/stringUtil';

const BaseballTracker = observer(({ item, tracker, typeData, isPopup }) => {
  const onClickData = () => {
    window.open(
      `/match/baseball/${item.id}?20210308`,
      `detail_${item.id}`,
      'width=1000px,height=901px,scrollbars=yes'
    );
  };
  const innerDom = () => {
    switch (tracker.status) {
      case 'progress':
        return <InnerProgress typeData={typeData} />;

      case 'final':
        return (
          <InnerFinal
            winnerTeam={item.winnerTeam}
            id={item.id}
            gameStatus={item.gameStatus}
            boardFlag={item.boardFlag}
            isPopup={isPopup}
          />
        );

      case 'ready':
        return (
          <InnerReady
            startDatetime={item.getStartDateTime}
            originStartDateTime={item.startDatetime}
          />
        );

      default:
        return (
          <InnerReady
            startDatetime={item.getStartDatetime}
            originStartDateTime={item.startDatetime}
          />
        );
    }
  };

  return (
    <div
      onClick={onClickData}
      className={clsx(style.tracker, style[tracker.class])}
    >
      {innerDom()}
      {(tracker.status === 'progress' || tracker.status === 'ready') &&
        (item.availableBroadcast.playText.length > 0 ? (
          <div className={style.text_relay}>
            <div className={style.cont}>
              {item.availableBroadcast.playText.includes('투수')
                ? item.availableBroadcast.playText
                : `${item.availableBroadcast.teamName} ${item.availableBroadcast.playText}`}
            </div>
          </div>
        ) : null)}
    </div>
  );
});

const InnerProgress = ({ typeData }) => {
  const {
    ball,
    currentBatter,
    currentPitcher,
    firstBaseOccupied,
    out,
    secondBaseOccupied,
    strike,
    thirdBaseOccupied
  } = typeData;

  const isIE = DeviceUtil.isIE();
  const baseOneRef = useRef();
  const baseTwoRef = useRef();
  const baseThreeRef = useRef();
  const [isInit, setInit] = useState(false);

  useEffect(() => {
    setInit(true);
  }, []);

  // 1루 애니메이션
  useEffect(() => {
    if (!isIE) {
      if (firstBaseOccupied) {
        if (!!baseOneRef.current) {
          baseOneRef.current.beginElement();
        }
      }
    }
  }, [firstBaseOccupied]);

  // 2루 애니메이션
  useEffect(() => {
    if (!isIE) {
      if (secondBaseOccupied) {
        if (!!baseTwoRef.current) {
          baseTwoRef.current.beginElement();
        }
      }
    }
  }, [secondBaseOccupied]);

  // 3루 애니메이션
  useEffect(() => {
    if (!isIE) {
      if (thirdBaseOccupied) {
        if (!!baseThreeRef.current) {
          baseThreeRef.current.beginElement();
        }
      }
    }
  }, [thirdBaseOccupied]);

  const playerDom = (player) => {
    if (!!player) {
      if (player.name.length > 5) {
        return (
          <>
            <em data-tip={player.name}>{player.name}</em>
            <ReactTooltip type="dark" place="bottom" effect="solid" />
          </>
        );
      } else {
        return <em>{player.name}</em>;
      }
    } else {
      return null;
    }
  };

  return (
    <div className={style.inner}>
      <div className={style.player}>
        <span className={style.defence}>{playerDom(currentPitcher)}</span>
        <span className={style.offence}>{playerDom(currentBatter)}</span>
      </div>
      <div className={style.base}>
        {/* ---------------- 1루 ---------------- */}
        {isIE ? (
          <span
            className={clsx(style.b1, firstBaseOccupied ? style.on : '')}
          ></span>
        ) : firstBaseOccupied ? (
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="12"
            height="12"
            className={style.b1}
            transform="rotate(45)"
          >
            <rect x="0" y="0" width="12" height="12" fill="#ed2026"></rect>
            <animate
              ref={baseOneRef}
              restart="always"
              attributeName="opacity"
              dur="0.5s"
              from={0}
              to={1}
              repeatCount="3"
            />
          </svg>
        ) : (
          <span className={style.b1}></span>
        )}
        {/* ---------------- 2루 ---------------- */}
        {isIE ? (
          <span
            className={clsx(style.b2, secondBaseOccupied ? style.on : '')}
          ></span>
        ) : secondBaseOccupied ? (
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="12"
            height="12"
            className={style.b2}
            transform="rotate(45)"
          >
            <rect x="0" y="0" width="12" height="12" fill="#ed2026"></rect>
            <animate
              ref={baseTwoRef}
              restart="always"
              attributeName="opacity"
              dur="0.5s"
              from={0}
              to={1}
              repeatCount="3"
            />
          </svg>
        ) : (
          <span className={style.b2}></span>
        )}
        {/* ---------------- 3루 ---------------- */}
        {isIE ? (
          <span
            className={clsx(style.b3, thirdBaseOccupied ? style.on : '')}
          ></span>
        ) : thirdBaseOccupied ? (
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="12"
            height="12"
            className={style.b3}
            transform="rotate(45)"
          >
            <rect x="0" y="0" width="12" height="12" fill="#ed2026"></rect>
            <animate
              ref={baseThreeRef}
              restart="always"
              attributeName="opacity"
              dur="0.5s"
              from={0}
              to={1}
              repeatCount="3"
            />
          </svg>
        ) : (
          <span className={style.b3}></span>
        )}
      </div>
      <div className={style.count}>
        <dl className={style.b}>
          <dt>B</dt>
          {isIE ? (
            <dd className={clsx(ball > 0 && style[`n${ball}`])}></dd>
          ) : (
            <BaseballCount count={ball} type={'ball'} />
          )}
        </dl>
        <dl className={style.s}>
          <dt>S</dt>
          {isIE ? (
            <dd className={clsx(strike > 0 && style[`n${strike}`])}></dd>
          ) : (
            <BaseballCount count={strike} type={'strike'} />
          )}
        </dl>

        <dl className={style.o}>
          <dt>O</dt>
          {isIE ? (
            <dd className={clsx(out > 0 && style[`n${out}`])}></dd>
          ) : (
            <BaseballCount count={out} type={'out'} />
          )}
        </dl>
      </div>
    </div>
  );
};

const InnerFinal = ({ id, gameStatus, boardFlag, winnerTeam, isPopup }) => {
  const finalMessage = useMemo(() => {
    if (gameStatus === 'FINAL') {
      return winnerTeam.difference === 0
        ? '무승부'
        : `${winnerTeam.difference} 점차 승리`;
    } else if (gameStatus === 'CANCEL' || gameStatus === 'SUSPENDED') {
      return '경기 취소';
    } else if (gameStatus === 'DELAY' || gameStatus === 'POSTPONED') {
      return '연기';
    } else if (gameStatus === 'PENDING' || gameStatus === 'CUT') {
      return '경기 중단';
    } else if (gameStatus === 'PAUSE') {
      return '일시 중단';
    }
  }, [gameStatus, winnerTeam]);

  const status = useMemo(() => {
    if (gameStatus === 'FINAL') {
      return winnerTeam.difference === 0 ? 'draw' : 'win';
    } else {
      return StringUtil.lower(gameStatus);
    }
  }, []);

  return (
    <div className={style.inner}>
      <SportsTeamLogo
        imgSize={'65'}
        imgUrl={status === 'win' ? winnerTeam.team.imgPath : null}
        isLazy={false}
        status={status}
        id={id}
      />
      <p className={style.info}>
        <em className={style.team}>{winnerTeam.team.name}</em>
        <em className={style.tx}>{finalMessage}</em>
      </p>
    </div>
  );
};

const InnerReady = ({ startDatetime, originStartDateTime }) => {
  const [timeView, setTimeView] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({});

  useEffect(() => {
    const intervalTime = setInterval(() => {
      setTimeRemaining(timeUtils.getTimeRemaining(originStartDateTime));
    }, 1000);

    return () => {
      clearInterval(intervalTime);
    };
  }, []);

  useEffect(() => {
    if (
      timeRemaining.days === 0 &&
      timeRemaining.hours === 0 &&
      timeRemaining.minutes <= 60
    ) {
      setTimeView(true);
    } else {
      setTimeView(false);
    }
  }, [timeRemaining, originStartDateTime]);

  return (
    <div className={style.inner}>
      <p className={style.time}>
        <em className={clsx(style.num, timeView && style.active)}>
          {timeView
            ? `00:${timeRemaining.minutes}:${timeRemaining.seconds}`
            : startDatetime}
        </em>
        <em className={style.tx}>{timeView ? '후' : ''} 경기 시작</em>
      </p>
    </div>
  );
};

export default BaseballTracker;
